import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import TaskQueue from "./TaskQueue";
import {
  Building2,
  FileText,
  Shield,
  Users,
  AlertTriangle,
  Settings,
} from "lucide-react";

const TASK_CONFIG = [
  {
    id: "update_organization",
    title: "Update Organization",
    description: "Configure organization details, locations, and departments",
    link: "/organization-setup",
    icon: Building2,
    checkKey: "organization_setup",
    phase: "PLAN",
    time: "~15 min",
  },
  {
    id: "complete_policy_wizard",
    title: "Complete Policy Wizard",
    description: "Establish BCM Policy foundation with AI assistance",
    link: "/bcm-policy",
    icon: FileText,
    checkKey: "policy_wizard",
    phase: "PLAN",
    time: "~30 min",
  },
  {
    id: "detailed_policy",
    title: "Detailed Policy",
    description: "Create comprehensive BCM policies and procedures",
    link: "/policies",
    icon: Shield,
    checkKey: "detailed_policies",
    phase: "PLAN",
    time: "~20 min",
  },
  {
    id: "stakeholder_register",
    title: "Update Stakeholder Register",
    description: "Identify and document key stakeholders",
    link: "/stakeholder-register",
    icon: Users,
    checkKey: "stakeholder_register",
    phase: "PLAN",
    time: "~10 min",
  },
  {
    id: "threat_profiling",
    title: "Complete Threat Profiling",
    description: "Identify and assess organizational threats",
    link: "/threat-profiling",
    icon: AlertTriangle,
    checkKey: "threat_profiling",
    phase: "PLAN",
    time: "~15 min",
  },
  {
    id: "bia_configuration",
    title: "Complete BIA Configuration",
    description: "Configure Business Impact Analysis parameters",
    link: "/bia-configuration",
    icon: Settings,
    checkKey: "bia_configuration",
    phase: "DO",
    time: "~20 min",
  },
];

export default function BCMProgramAttention() {
  const { api, user } = useAuth();
  const [taskStatus, setTaskStatus] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.org_id) {
      checkTaskCompletion();
    }
  }, [user?.org_id]);

  const checkTaskCompletion = async () => {
    setLoading(true);
    try {
      const status = {};
      const orgRes = await api.get("/bcm-admin/organization").catch(() => null);
      const locRes = await api.get("/bcm-admin/locations").catch(() => ({ data: [] }));
      const deptRes = await api.get("/bcm-admin/departments").catch(() => ({ data: [] }));
      status.organization_setup = orgRes?.data?.name && (locRes?.data?.length > 0 || deptRes?.data?.length > 0);

      const wizardRes = await api.get("/bcm-policy/wizard-state").catch(() => null);
      status.policy_wizard = wizardRes?.data?.current_phase === "complete" || wizardRes?.data?.phases?.foundation?.completed;

      const policiesRes = await api.get("/policies").catch(() => ({ data: [] }));
      status.detailed_policies = policiesRes?.data?.length > 0;

      const stakeholderRes = await api.get("/stakeholders").catch(() => ({ data: [] }));
      status.stakeholder_register = stakeholderRes?.data?.length > 0;

      const threatRes = await api.get("/threat-profiles").catch(() => ({ data: [] }));
      status.threat_profiling = threatRes?.data?.length > 0;

      const biaRes = await api.get("/bia-settings").catch(() => null);
      status.bia_configuration = biaRes?.data?.impact_categories?.length > 0 || biaRes?.data?.time_periods?.length > 0;

      setTaskStatus(status);
    } catch (error) {
      console.error("Failed to check task completion:", error);
    } finally {
      setLoading(false);
    }
  };

  // Transform TASK_CONFIG into the format TaskQueue expects
  const tasks = TASK_CONFIG.map((task) => ({
    ...task,
    isComplete: !!taskStatus[task.checkKey],
  }));

  return (
    <TaskQueue
      title="My Program To-Do List"
      tasks={tasks}
      loading={loading}
      completeTitle="BCM Program Setup Complete!"
      completeDescription="All foundational tasks have been completed."
      testId="bcm-program-attention"
    />
  );
}
